const ELLIPSIS = "ellipsis";

export function getPaginationItems(currentPage, totalPages, siblingCount = 1) {
  const total = Math.max(1, Number(totalPages) || 1);
  const current = Math.min(Math.max(1, Number(currentPage) || 1), total);
  const siblings = Math.max(0, Number(siblingCount) || 0);
  const visibleSlots = siblings * 2 + 5;

  if (total <= visibleSlots) {
    return Array.from({ length: total }, (_, index) => index + 1);
  }

  const left = Math.max(current - siblings, 2);
  const right = Math.min(current + siblings, total - 1);
  const showLeftEllipsis = left > 2;
  const showRightEllipsis = right < total - 1;

  if (!showLeftEllipsis) {
    const end = Math.min(total - 1, 3 + siblings * 2);
    return [
      ...Array.from({ length: end }, (_, index) => index + 1),
      ELLIPSIS,
      total,
    ];
  }

  if (!showRightEllipsis) {
    const start = Math.max(2, total - (2 + siblings * 2));
    return [
      1,
      ELLIPSIS,
      ...Array.from({ length: total - start + 1 }, (_, index) => start + index),
    ];
  }

  return [
    1,
    ELLIPSIS,
    ...Array.from({ length: right - left + 1 }, (_, index) => left + index),
    ELLIPSIS,
    total,
  ];
}

function PageItems({ items, currentPage, onPageChange }) {
  return items.map((item, index) =>
    item === ELLIPSIS ? (
      <span
        key={`${item}-${index}`}
        className="flex h-8 min-w-5 items-center justify-center px-0.5 text-gray-500 sm:min-w-8"
        aria-hidden="true"
      >
        &hellip;
      </span>
    ) : (
      <button
        key={item}
        type="button"
        onClick={() => onPageChange(item)}
        aria-label={`Go to page ${item}`}
        aria-current={item === currentPage ? "page" : undefined}
        className={`h-8 min-w-8 rounded border px-1.5 text-sm disabled:cursor-not-allowed disabled:opacity-50 sm:px-2 ${
          item === currentPage
            ? "border-[#152259] bg-[#152259] text-white"
            : "border-gray-300 bg-white text-gray-700 hover:bg-gray-100"
        }`}
      >
        {item}
      </button>
    )
  );
}

export default function Pagination({
  currentPage,
  totalPages,
  onPageChange,
  totalItems,
  pageSize,
  itemLabel = "items",
  className = "",
}) {
  const total = Math.max(1, Number(totalPages) || 1);
  const current = Math.min(Math.max(1, Number(currentPage) || 1), total);
  const hasSummary = Number.isFinite(totalItems) && Number.isFinite(pageSize);
  const start = totalItems === 0 ? 0 : (current - 1) * pageSize + 1;
  const end = hasSummary ? Math.min(current * pageSize, totalItems) : 0;
  const buttonClass =
    "h-8 whitespace-nowrap rounded border border-gray-300 bg-white px-2 text-sm text-gray-700 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50 sm:px-3";

  if (total <= 1 && !hasSummary) return null;

  return (
    <nav
      className={`flex min-w-0 max-w-full flex-col items-center gap-2 text-sm text-gray-700 sm:flex-row sm:flex-wrap sm:justify-between ${className}`}
      aria-label="Pagination"
    >
      {hasSummary && (
        <span className="max-w-full text-center sm:text-left">
          Showing {start}-{end} of {totalItems} {itemLabel}
        </span>
      )}

      {total > 1 && (
        <div className="flex max-w-full items-center justify-center gap-1 sm:gap-1.5">
          <button
            type="button"
            onClick={() => onPageChange(current - 1)}
            disabled={current <= 1}
            className={buttonClass}
            aria-label="Go to previous page"
          >
            <span className="sm:hidden">Prev</span>
            <span className="hidden sm:inline">Previous</span>
          </button>

          <div className="flex items-center gap-1 sm:hidden">
            <PageItems
              items={getPaginationItems(current, total, 0)}
              currentPage={current}
              onPageChange={onPageChange}
            />
          </div>
          <div className="hidden items-center gap-1.5 sm:flex">
            <PageItems
              items={getPaginationItems(current, total, 1)}
              currentPage={current}
              onPageChange={onPageChange}
            />
          </div>

          <button
            type="button"
            onClick={() => onPageChange(current + 1)}
            disabled={current >= total}
            className={buttonClass}
            aria-label="Go to next page"
          >
            Next
          </button>
        </div>
      )}
    </nav>
  );
}
